import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckIcon,
  ClockIcon,
  SewingPinIcon,
  VideoIcon,
} from "@radix-ui/react-icons";
import moment from "moment-timezone";
import {
  ApiError,
  ClassesService,
  OrganizationMeetingLocationType,
  OrganizationPublicResponse,
  OrganizationWebPagePublicResponse,
  PublicClass,
  PublicEnrolled,
} from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import { revealTop } from "util/embed";
import {
  dateRange,
  oneSessionLabel,
  plural,
  weeklyLongLabel,
} from "components/Classes/classText";
import { FactRow } from "./BookingDone";
import PhoneVerifyStep from "./PhoneVerifyStep";
import { PersonPhoto } from "./PublicShell";
import WhoStep, { Enrollees } from "./WhoStep";
import {
  loadVerified,
  saveVerified,
  tint,
  Verified,
  zoneLabel,
} from "./publicBooking";

/** "Maya", "Maya and Leo", "Maya, Leo and Sam". */
export const joinNames = (names: string[]) =>
  names.length > 1
    ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`
    : names[0] ?? "";

/** A one-session class's date and time, in the org's zone. */
const oneSession = (klass: PublicClass) =>
  oneSessionLabel(klass, klass.timezone);

/**
 * "Saturdays 10:00 to 11:30am", or "Sat, Oct 10 · 10:00am to 12:00pm" for
 * one session, with the org's zone when it isn't the visitor's.
 */
function whenLine(klass: PublicClass) {
  const zone =
    moment.tz.guess() === klass.timezone
      ? ""
      : `, ${zoneLabel(klass.timezone)}`;
  return `${oneSession(klass) ?? weeklyLongLabel(klass.weekly_times)}${zone}`;
}

/**
 * "Oct 10 to Dec 12 · 10 sessions", or "Started Sep 15 · 9 sessions left".
 * Nothing for one session: its date is in its when line.
 */
function datesLine(klass: PublicClass) {
  if (oneSession(klass)) return undefined;
  return klass.started
    ? `Started ${moment(klass.first_date).format("MMM D")} · ${plural(
        klass.sessions_left,
        "session"
      )} left`
    : `${dateRange(klass.first_date, klass.last_date)} · ${plural(
        klass.total_sessions,
        "session"
      )}`;
}

const isFull = (klass: PublicClass) => klass.seats_left === 0;

function PlaceRow({ klass }: { klass: PublicClass }) {
  if (!klass.location) return null;
  const online =
    klass.location.location_type === OrganizationMeetingLocationType.ONLINE;
  return (
    <FactRow icon={online ? <VideoIcon /> : <SewingPinIcon />}>
      {[klass.location.name, klass.location.city].filter(Boolean).join(", ")}
    </FactRow>
  );
}

function Tutors({ klass, accent }: { klass: PublicClass; accent: string }) {
  if (!klass.tutors.length) return null;
  return (
    <div className="flex items-center gap-3 text-[15px] text-gray-700">
      <span className="flex -space-x-1.5">
        {klass.tutors.map((tutor) => (
          <span key={tutor.id} className="rounded-full ring-2 ring-white">
            <PersonPhoto
              url={tutor.photo_url}
              name={`${tutor.first_name} ${tutor.last_name}`}
              size={24}
              accent={accent}
            />
          </span>
        ))}
      </span>
      {joinNames(klass.tutors.map((t) => `${t.first_name} ${t.last_name}`))}
    </div>
  );
}

/** "5 of 12 spots left" over a bar; nothing when there is no seat limit. */
function Seats({ klass, accent }: { klass: PublicClass; accent: string }) {
  if (!klass.capacity || klass.seats_left == null) return null;
  const full = isFull(klass);
  const taken = klass.capacity - klass.seats_left;
  return (
    <div>
      {full ? (
        <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500">
          Full
        </span>
      ) : (
        <p className="text-sm font-semibold" style={{ color: accent }}>
          {klass.seats_left} of {klass.capacity} spots left
        </p>
      )}
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, (taken / klass.capacity) * 100)}%`,
            backgroundColor: full ? "#9CA3AF" : accent,
          }}
        />
      </div>
    </div>
  );
}

function SubjectTag({ name, accent }: { name: string; accent: string }) {
  return (
    <span
      className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ backgroundColor: tint(accent), color: accent }}
    >
      {name}
    </span>
  );
}

function ClassCard({
  klass,
  accent,
  onEnroll,
}: {
  klass: PublicClass;
  accent: string;
  onEnroll: () => void;
}) {
  const full = isFull(klass);
  return (
    <div className="flex flex-col gap-5 rounded-xl border border-gray-200 p-5 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-bold">{klass.name}</h2>
          <SubjectTag name={klass.subject} accent={accent} />
        </div>
        <div className="mt-2 flex flex-col gap-1.5">
          <FactRow icon={<CalendarIcon />}>{whenLine(klass)}</FactRow>
          {datesLine(klass) && (
            <FactRow icon={<ClockIcon />}>{datesLine(klass)}</FactRow>
          )}
          <PlaceRow klass={klass} />
          <Tutors klass={klass} accent={accent} />
        </div>
        {klass.description && (
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-600 line-clamp-2">
            {klass.description}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-3 sm:w-44 sm:shrink-0">
        <Seats klass={klass} accent={accent} />
        <button
          type="button"
          disabled={full}
          onClick={onEnroll}
          className="w-full rounded-lg py-2.5 font-semibold text-white disabled:bg-gray-200 disabled:text-gray-400"
          style={full ? undefined : { backgroundColor: accent }}
        >
          {full ? "Full" : "Enroll"}
        </button>
      </div>
    </div>
  );
}

/** The class in a gray box above the enrollment steps. */
function ClassSummary({
  klass,
  accent,
}: {
  klass: PublicClass;
  accent: string;
}) {
  const facts = [
    plural(klass.sessions_to_join, "session"),
    klass.location?.name,
    !klass.capacity || klass.seats_left == null
      ? undefined
      : `${plural(klass.seats_left, "spot")} left`,
  ].filter(Boolean);
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-lg font-bold">{klass.name}</p>
        <SubjectTag name={klass.subject} accent={accent} />
      </div>
      <p className="mt-1 text-sm text-gray-700">
        {oneSession(klass)
          ? whenLine(klass)
          : `${whenLine(klass)} · ${dateRange(
              klass.first_date,
              klass.last_date
            )}`}
      </p>
      <p className="text-sm text-gray-500">{facts.join(" · ")}</p>
    </div>
  );
}

/** You're enrolled: who joined how many sessions, and the schedule. */
function EnrollDone({
  klass,
  enrolled,
  org,
  accent,
  onBack,
}: {
  klass: PublicClass;
  enrolled: PublicEnrolled[];
  org: OrganizationPublicResponse;
  accent: string;
  onBack: () => void;
}) {
  return (
    <div className="mx-auto max-w-md px-5 py-10 text-center">
      <div
        className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
        style={{ backgroundColor: tint(accent), color: accent }}
      >
        <CheckIcon width={30} height={30} />
      </div>
      <h1 className="mt-4 text-2xl font-bold">You're enrolled</h1>
      <p className="mt-2 text-gray-600">
        {enrolled
          .map(
            (e) =>
              `${e.first_name} joined ${plural(e.sessions, "session")}` +
              (e.sessions === 0 ? " (already enrolled)" : "")
          )
          .join(". ")}
        .
      </p>
      <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 text-left">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3">
          <p className="font-semibold">{klass.name}</p>
          <SubjectTag name={klass.subject} accent={accent} />
        </div>
        <div className="mt-3 flex flex-col gap-2">
          <FactRow icon={<CalendarIcon />}>{whenLine(klass)}</FactRow>
          {!oneSession(klass) && (
            <FactRow icon={<ClockIcon />}>
              {dateRange(klass.first_date, klass.last_date)}
            </FactRow>
          )}
          <PlaceRow klass={klass} />
          <Tutors klass={klass} accent={accent} />
        </div>
      </div>
      <button
        type="button"
        onClick={onBack}
        className="mt-6 w-full rounded-lg border-2 py-3 font-semibold"
        style={{ borderColor: accent, color: accent }}
      >
        See all classes
      </button>
      <p className="mt-3 text-xs text-gray-500">
        {org.name} will be in touch about billing.
      </p>
    </div>
  );
}

/** The class, the phone code, who is enrolling, then You're enrolled. */
function EnrollFlow({
  klass,
  org,
  page,
  onBack,
  onChanged,
}: {
  klass: PublicClass;
  org: OrganizationPublicResponse;
  page: OrganizationWebPagePublicResponse;
  onBack: () => void;
  onChanged: () => void;
}) {
  const accent = page.primary_color;
  const [verified, setVerified] = useState<Verified | undefined>(() =>
    loadVerified(org.id)
  );
  const [phoneStep, setPhoneStep] = useState({ key: 0, prefill: "" });
  const resetPhone = (prefill: string) => {
    saveVerified(org.id, undefined);
    setVerified(undefined);
    setPhoneStep((current) => ({ key: current.key + 1, prefill }));
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [enrolled, setEnrolled] = useState<PublicEnrolled[]>();

  const enroll = (enrollees: Enrollees) => {
    if (!verified) return;
    setBusy(true);
    setError(undefined);
    ClassesService.enrollInClassPublicly({
      classId: klass.id,
      requestBody: { org_id: org.id, token: verified.token, ...enrollees },
    })
      .then((result) => {
        // The family as it is now, for the next class.
        const refreshed = { ...verified, ...result.family };
        saveVerified(org.id, refreshed);
        setVerified(refreshed);
        setEnrolled(result.enrolled);
        revealTop();
        onChanged();
      })
      .catch((e: ApiError) => {
        if (e.status === 401) {
          resetPhone(verified.phone);
          setError("Your code has expired. Ask for a new code.");
        } else {
          setError(errorText(e, "That didn't work. Try again."));
          if (e.status === 409) onChanged();
        }
      })
      .finally(() => setBusy(false));
  };

  if (enrolled)
    return (
      <EnrollDone
        klass={klass}
        enrolled={enrolled}
        org={org}
        accent={accent}
        onBack={onBack}
      />
    );

  return (
    <div className="mx-auto max-w-[600px] px-5 pt-5 pb-9 sm:px-9">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900"
      >
        <ArrowLeftIcon /> All classes
      </button>
      <ClassSummary klass={klass} accent={accent} />
      <div className="mt-5">
        <PhoneVerifyStep
          key={phoneStep.key}
          orgId={org.id}
          accent={accent}
          verified={verified}
          initialPhone={phoneStep.prefill}
          onVerified={(v) => {
            saveVerified(org.id, v);
            setVerified(v);
            setError(undefined);
          }}
          onChangeNumber={() => resetPhone("")}
        />
      </div>
      {verified && (
        <WhoStep
          key={verified.token}
          multiple
          org={org}
          verified={verified}
          returningOnly={page.booking.returning_families_only}
          accent={accent}
          busy={busy}
          label={(names) =>
            names.length
              ? `Enroll ${joinNames(names)} in ${plural(
                  klass.sessions_to_join,
                  "session"
                )}`
              : "Enroll"
          }
          onEnroll={enroll}
        />
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/** The org page's Classes tab: one card per open class, then enrollment. */
export default function PublicClasses({
  page,
  org,
}: {
  page: OrganizationWebPagePublicResponse;
  org: OrganizationPublicResponse;
}) {
  const [params, setParams] = useSearchParams();
  const [classes, setClasses] = useState<PublicClass[]>();
  const [failed, setFailed] = useState(false);
  const accent = page.primary_color;

  const load = useCallback(() => {
    ClassesService.getPublicClasses({ orgId: org.id })
      .then((loaded) => {
        setClasses(loaded);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, [org.id]);
  useEffect(load, [load]);

  const enrollId = Number(params.get("enroll")) || undefined;
  // The class being enrolled in stays on screen (with any error, or You're
  // enrolled) even if a reload no longer lists it, for example once closed.
  const opened = useRef<PublicClass>();
  const listed = classes?.find((c) => c.id === enrollId);
  if (listed) opened.current = listed;
  const enrolling =
    listed ?? (opened.current?.id === enrollId ? opened.current : undefined);
  const show = (classId?: number) => {
    setParams(classId ? { enroll: String(classId) } : {});
    window.scrollTo(0, 0);
    revealTop(); // in a widget, the website scrolls to the widget's top
  };

  if (failed)
    return (
      <p className="px-9 py-12 text-center text-gray-600">
        Classes didn't load. Check your connection and try again.
      </p>
    );
  if (!classes)
    return (
      <div className="animate-pulse space-y-3 px-5 py-6 sm:px-9">
        <div className="h-36 rounded-xl bg-gray-100" />
        <div className="h-36 rounded-xl bg-gray-50" />
      </div>
    );
  if (enrolling)
    return (
      <EnrollFlow
        klass={enrolling}
        org={org}
        page={page}
        onBack={() => show()}
        onChanged={load}
      />
    );
  if (!classes.length)
    return (
      <p className="px-9 py-12 text-center text-gray-600">
        No classes are open for enrollment right now.
      </p>
    );
  return (
    <div className="flex flex-col gap-3 px-5 pt-5 pb-6 sm:px-9">
      {classes.map((klass) => (
        <ClassCard
          key={klass.id}
          klass={klass}
          accent={accent}
          onEnroll={() => show(klass.id)}
        />
      ))}
    </div>
  );
}
